import { useState, useEffect, useCallback } from "react";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import Modal from "../modal/modal";
import Dropdown from "../modal/dropdown";
import ConfirmModal from "../employee/delete_modal";
import Can from "../common/can";
import { toast } from "react-toastify";
import { getClients, createClient, updateClient, deleteClient } from "../services/clientservice";
import { parseFieldErrors } from "../../utils/errors";
import { useAuth } from "../context/authcontext";
import { ClientStatus, ClientIndustry } from "../../constants/constants";
import Pagination from "../common/pagination";

const INDUSTRY_OPTIONS = [
  { label: "Technology", value: ClientIndustry.TECHNOLOGY },
  { label: "Finance & Banking", value: ClientIndustry.FINANCE },
  { label: "Healthcare", value: ClientIndustry.HEALTHCARE },
  { label: "Retail & E-Commerce", value: ClientIndustry.RETAIL },
  { label: "Manufacturing", value: ClientIndustry.MANUFACTURING },
  { label: "Education", value: ClientIndustry.EDUCATION },
  { label: "Other", value: ClientIndustry.OTHER },
];

const Clients = () => {
  const { hasAnyPermission, hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const canShowActions = isAdmin || hasAnyPermission(["client.view", "client.update", "client.delete"]);
  const [clients, setClients]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);
  const [totalCount, setTotal]    = useState(0);
  const PAGE_SIZE = 20;

  const [search, setSearch]               = useState("");
  const [statusFilter, setStatusFilter]   = useState("All");
  const [openModal, setOpenModal]         = useState(false);
  const [selected, setSelected]           = useState(null);
  const [viewTarget, setViewTarget]       = useState(null);
  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [saving, setSaving]               = useState(false);
  const [error, setError]                 = useState("");
  const [fieldErrors, setFieldErrors]     = useState({});

  const getStatusBadgeClass = (status) => {
    if (status === ClientStatus.ACTIVE) return "active";
    if (status === ClientStatus.INACTIVE) return "inactive";
    if (status === ClientStatus.ARCHIVED) return "archived";
    return "";
  };

  const [form, setForm] = useState({
    company_name: "", client_code: "", industry: "", website: "",
    gst_number: "", address: "", city: "", state: "", country: "",
    status: ClientStatus.ACTIVE, notes: "",
  });

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: PAGE_SIZE };
      if (search)                   params.search = search;
      const statusMap = {
        "Active": ClientStatus.ACTIVE,
        "Inactive": ClientStatus.INACTIVE,
        "Archived": ClientStatus.ARCHIVED
      };
      if (statusFilter !== "All")   params.status = statusMap[statusFilter];
      const res = await getClients(params);
      const data = res.data || res;
      const meta = res.meta || {};
      setClients(Array.isArray(data) ? data : data.results || []);
      setTotal(meta.count ?? data.count ?? (Array.isArray(data) ? data.length : 0));
    } catch { toast.error("Failed to load clients"); }
    finally { setLoading(false); }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchClients(); }, [fetchClients]);
  useEffect(() => { setPage(1); }, [search, statusFilter]);

  const openAdd = () => {
    setSelected(null);
    setForm({ company_name: "", client_code: "", industry: "", website: "", gst_number: "", address: "", city: "", state: "", country: "", status: ClientStatus.ACTIVE, notes: "" });
    setError("");
    setFieldErrors({});
    setOpenModal(true);
  };

  const openEdit = (c) => {
    setSelected(c);
    setForm({ 
        company_name: c.company_name || "", 
        client_code: c.client_code || "", 
        industry: c.industry || "", 
        website: c.website || "", 
        gst_number: c.gst_number || "", 
        address: c.address_street || c.address || "", 
        city: c.address_city || c.city || "", 
        state: c.address_state || c.state || "", 
        country: c.address_country || c.country || "", 
        status: c.status || ClientStatus.ACTIVE, 
        notes: c.description || c.notes || "" 
    });
    setError("");
    setFieldErrors({});
    setOpenModal(true);
  };

  const handleSave = async () => {
    if (!form.company_name) {
      setFieldErrors({ company_name: "Company name is required" });
      return setError("Please check the field errors below.");
    }
    setSaving(true);
    setError("");
    setFieldErrors({});
    try {
      const payload = {
        company_name: form.company_name,
        industry: form.industry || "other",
        status: form.status,
        website: form.website,
        gst_number: form.gst_number,
        description: form.notes,
        address_street: form.address,
        address_city: form.city,
        address_state: form.state,
        address_country: form.country,
      };

      if (selected) {
        await updateClient(selected.id, payload);
        toast.success("Client updated");
      } else {
        await createClient(payload);
        toast.success("Client created");
      }
      await fetchClients();
      setOpenModal(false);
    } catch (err) {
      setFieldErrors(parseFieldErrors(err));
      setError(err.response?.data?.message || "Failed to save client");
    } finally { setSaving(false); }
  };

  const confirmDelete = async (password) => {
    try {
      await deleteClient(deleteTarget.id, password);
      toast.success("Client deleted successfully");
      await fetchClients();
      setDeleteTarget(null);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.detail || "Failed to delete client: Incorrect password or permission denied";
      toast.error(msg);
      throw new Error(msg);
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div className="container">
      <div className="page-header">
        <h2>Clients</h2>
        <Can perm="client.create">
          <button className="primary-btn" onClick={openAdd}>Add Client</button>
        </Can>
      </div>

      <div className="filters-card single">
        <input className="search-input" placeholder="🔍 Search clients..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <Dropdown options={["All", "Active", "Inactive"]} value={statusFilter} onChange={setStatusFilter} placeholder="Status" />
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading clients…</p></div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Company</th>
              <th>Industry</th>
              <th>GST</th>
              <th>City</th>
              <th>Status</th>
              {canShowActions && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 ? (
              <tr><td colSpan={canShowActions ? "7" : "6"}><div className="empty-state"><p>No Clients Found</p></div></td></tr>
            ) : (
              clients.map((c) => (
                <tr key={c.id}>
                  <td>{c.client_code}</td>
                  <td><strong>{c.company_name}</strong></td>
                  <td>{c.industry || "—"}</td>
                  <td>{c.gst_number || "—"}</td>
                  <td>{c.address_city || "—"}</td>
                  <td><span className={`emp-status ${getStatusBadgeClass(c.status)}`}>{c.status_display || c.status}</span></td>
                  {canShowActions && (
                    <td className="action-icons">
                      <Can perm="client.view">
                        <button className="icon-btn" style={{ color: "var(--primary)" }} onClick={() => setViewTarget(c)}><FaEye /></button>
                      </Can>
                      <Can perm="client.update">
                        <button className="icon-btn edit" onClick={() => openEdit(c)}><FaEdit /></button>
                      </Can>
                      <Can perm="client.delete">
                        <button className="icon-btn delete" onClick={() => setDeleteTarget(c)}><FaTrash /></button>
                      </Can>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      <Pagination page={page} totalPages={totalPages} setPage={setPage} />

      {openModal && (
        <Modal title={selected ? "Edit Client" : "Add Client"} onClose={() => setOpenModal(false)}>
          {error && <div className="form-error">{error}</div>}
          <div className="employee-form">
            <div className="employee-form-scroll">
              <h4 className="section-title">Company Information</h4>
              <div className="grid-2">
                <div>
                  <input className={fieldErrors.company_name ? "input-error" : ""} placeholder="Company Name *" value={form.company_name} onChange={(e) => { setForm({ ...form, company_name: e.target.value }); if (fieldErrors.company_name) setFieldErrors({ ...fieldErrors, company_name: null }); }} />
                  {fieldErrors.company_name && <div className="field-error">{fieldErrors.company_name}</div>}
                </div>
                <div>
                  <input className={fieldErrors.client_code ? "input-error" : ""} placeholder="Client Code" value={form.client_code} onChange={(e) => { setForm({ ...form, client_code: e.target.value }); if (fieldErrors.client_code) setFieldErrors({ ...fieldErrors, client_code: null }); }} />
                  {fieldErrors.client_code && <div className="field-error">{fieldErrors.client_code}</div>}
                </div>
                <div>
                  <Dropdown
                    options={INDUSTRY_OPTIONS}
                    value={form.industry}
                    onChange={(v) => { setForm({ ...form, industry: v }); if (fieldErrors.industry) setFieldErrors({ ...fieldErrors, industry: null }); }}
                    placeholder="Select Industry"
                  />
                  {fieldErrors.industry && <div className="field-error">{fieldErrors.industry}</div>}
                </div>
                <div>
                  <input className={fieldErrors.website ? "input-error" : ""} placeholder="Website" value={form.website} onChange={(e) => { setForm({ ...form, website: e.target.value }); if (fieldErrors.website) setFieldErrors({ ...fieldErrors, website: null }); }} />
                  {fieldErrors.website && <div className="field-error">{fieldErrors.website}</div>}
                </div>
                <div>
                  <input className={fieldErrors.gst_number ? "input-error" : ""} placeholder="GST Number" value={form.gst_number} onChange={(e) => { setForm({ ...form, gst_number: e.target.value }); if (fieldErrors.gst_number) setFieldErrors({ ...fieldErrors, gst_number: null }); }} />
                  {fieldErrors.gst_number && <div className="field-error">{fieldErrors.gst_number}</div>}
                  <div className="input-group">
                    <label>Status <span className="req">*</span></label>
                    <Dropdown 
                      options={[
                        { label: "Active", value: ClientStatus.ACTIVE }, 
                        { label: "Inactive", value: ClientStatus.INACTIVE },
                        { label: "Archived", value: ClientStatus.ARCHIVED }
                      ]}
                      value={form.status}
                      onChange={(v) => { setForm({ ...form, status: v }); if (fieldErrors.status) setFieldErrors({ ...fieldErrors, status: null }); }}
                      placeholder="Status"
                    />
                  </div>
                  {fieldErrors.status && <div className="field-error">{fieldErrors.status}</div>}
                </div>
              </div>
              <h4 className="section-title">Address</h4>
              <div className="grid-2">
                <div>
                  <input className={fieldErrors.address ? "input-error" : ""} placeholder="Address" value={form.address} onChange={(e) => { setForm({ ...form, address: e.target.value }); if (fieldErrors.address) setFieldErrors({ ...fieldErrors, address: null }); }} />
                  {fieldErrors.address && <div className="field-error">{fieldErrors.address}</div>}
                </div>
                <div>
                  <input className={fieldErrors.city ? "input-error" : ""} placeholder="City" value={form.city} onChange={(e) => { setForm({ ...form, city: e.target.value }); if (fieldErrors.city) setFieldErrors({ ...fieldErrors, city: null }); }} />
                  {fieldErrors.city && <div className="field-error">{fieldErrors.city}</div>}
                </div>
                <div>
                  <input className={fieldErrors.state ? "input-error" : ""} placeholder="State" value={form.state} onChange={(e) => { setForm({ ...form, state: e.target.value }); if (fieldErrors.state) setFieldErrors({ ...fieldErrors, state: null }); }} />
                  {fieldErrors.state && <div className="field-error">{fieldErrors.state}</div>}
                </div>
                <div>
                  <input className={fieldErrors.country ? "input-error" : ""} placeholder="Country" value={form.country} onChange={(e) => { setForm({ ...form, country: e.target.value }); if (fieldErrors.country) setFieldErrors({ ...fieldErrors, country: null }); }} />
                  {fieldErrors.country && <div className="field-error">{fieldErrors.country}</div>}
                </div>
              </div>
              <h4 className="section-title">Notes</h4>
              <div>
                <textarea className={fieldErrors.notes ? "input-error" : ""} placeholder="Notes" value={form.notes} onChange={(e) => { setForm({ ...form, notes: e.target.value }); if (fieldErrors.notes) setFieldErrors({ ...fieldErrors, notes: null }); }} />
                {fieldErrors.notes && <div className="field-error">{fieldErrors.notes}</div>}
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <div className="modal-actions">
              <button className="primary" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
              <button className="secondary" onClick={() => setOpenModal(false)} disabled={saving}>Cancel</button>
            </div>
          </div>
        </Modal>
      )}

      {viewTarget && (
        <Modal title="Client Details" onClose={() => setViewTarget(null)}>
          <div className="employee-form" style={{ padding: "0 20px 20px" }}>
            <div className="grid-2">
              <div>
                <strong style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>Company Name</strong>
                <p style={{ margin: "4px 0 16px 0", color: "var(--text-main)" }}>{viewTarget.company_name}</p>
              </div>
              <div>
                <strong style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>Client Code</strong>
                <p style={{ margin: "4px 0 16px 0", color: "var(--text-main)" }}>{viewTarget.client_code}</p>
              </div>
              <div>
                <strong style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>Industry</strong>
                <p style={{ margin: "4px 0 16px 0", color: "var(--text-main)" }}>{viewTarget.industry_display || viewTarget.industry || "—"}</p>
              </div>
              <div>
                <strong style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>Status</strong>
                <p style={{ margin: "4px 0 16px 0" }}><span className={`emp-status ${getStatusBadgeClass(viewTarget.status)}`}>{viewTarget.status_display || viewTarget.status}</span></p>
              </div>
              <div>
                <strong style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>GST Number</strong>
                <p style={{ margin: "4px 0 16px 0", color: "var(--text-main)" }}>{viewTarget.gst_number || "—"}</p>
              </div>
              <div>
                <strong style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>Website</strong>
                <p style={{ margin: "4px 0 16px 0", color: "var(--text-main)" }}>
                  {viewTarget.website ? <a href={viewTarget.website} target="_blank" rel="noreferrer" style={{ color: "var(--primary)" }}>{viewTarget.website}</a> : "—"}
                </p>
              </div>
            </div>
            
            <h4 className="section-title" style={{ marginTop: "16px", marginBottom: "12px", borderBottom: "1px solid var(--border-color)", paddingBottom: "8px" }}>Address Information</h4>
            <div className="grid-2">
              <div style={{ gridColumn: "span 2" }}>
                <strong style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>Address</strong>
                <p style={{ margin: "4px 0 16px 0", color: "var(--text-main)" }}>{viewTarget.address_street || "—"}</p>
              </div>
              <div>
                <strong style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>City</strong>
                <p style={{ margin: "4px 0 16px 0", color: "var(--text-main)" }}>{viewTarget.address_city || "—"}</p>
              </div>
              <div>
                <strong style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>State</strong>
                <p style={{ margin: "4px 0 16px 0", color: "var(--text-main)" }}>{viewTarget.address_state || "—"}</p>
              </div>
              <div>
                <strong style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>Country</strong>
                <p style={{ margin: "4px 0 16px 0", color: "var(--text-main)" }}>{viewTarget.address_country || "—"}</p>
              </div>
            </div>

            <h4 className="section-title" style={{ marginTop: "16px", marginBottom: "12px", borderBottom: "1px solid var(--border-color)", paddingBottom: "8px" }}>Additional Information</h4>
            <div>
              <strong style={{ color: "var(--text-secondary)", fontSize: "0.85rem" }}>Notes / Description</strong>
              <p style={{ margin: "4px 0 16px 0", color: "var(--text-main)", whiteSpace: "pre-wrap" }}>{viewTarget.description || "—"}</p>
            </div>
          </div>
          <div className="modal-footer">
            <div className="modal-actions">
              <button className="secondary" onClick={() => setViewTarget(null)}>Close</button>
            </div>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Client"
          message={`Delete "${deleteTarget.company_name}"?`}
          confirmText="Delete"
          type="danger"
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default Clients;
